import { useCallback, useEffect, useState } from 'react';
import { Copy, DoorOpen, Plus, Power, AlertTriangle, CheckCircle2, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmModal from '../ui/ConfirmModal';

function formatDateTime(value) {
    if (!value) return 'Mai';
    const d = new Date(String(value).replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString('it-IT');
}

/**
 * API-1: terminali (centraline) del controllo accessi di una palestra, solo admin.
 * Il token del terminale si vede una sola volta, subito dopo la creazione: va inserito nella configurazione
 * della centralina insieme all'indirizzo dell'endpoint.
 * Centraline LabKey ("Delega su dispositivo esterno"): niente token, si riconoscono dal seriale e firmano le
 * chiamate con PassKey + password WiFi della centralina, che quindi salviamo qui.
 */

const inputClass = 'rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white';
const emptyLabkey = { unique: '', wifipass: '', passkey: '', relay: '1' };

function LabkeyFields({ value, onChange, editing = false }) {
    const set = (key) => (e) => onChange({ ...value, [key]: e.target.value });
    return (
        <div className="grid gap-2 sm:grid-cols-2">
            {!editing && (
                <input type="text" value={value.unique} onChange={set('unique')} placeholder="Seriale (es. LABKEY-59059A26)"
                    maxLength={40} className={inputClass} data-testid="labkey-unique" />
            )}
            <input type="password" value={value.wifipass} onChange={set('wifipass')} autoComplete="new-password"
                placeholder={editing ? 'Nuova password WiFi (vuoto = invariata)' : 'Password WiFi della centralina'}
                maxLength={100} className={inputClass} data-testid="labkey-wifipass" />
            <input type="text" value={value.passkey} onChange={set('passkey')} maxLength={32} autoComplete="off"
                placeholder={editing ? 'Nuova PassKey, 32 caratteri (vuoto = invariata)' : 'PassKey, 32 caratteri (vuoto = la genero io)'}
                className={`${inputClass} font-mono`} data-testid="labkey-passkey" />
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                Relè da aprire
                <input type="number" min={1} max={16} value={value.relay} onChange={set('relay')}
                    className={`${inputClass} w-20`} data-testid="labkey-relay" />
            </label>
        </div>
    );
}
export default function AdminGymAccessDevices({ gymId }) {
    const { getAuthenticatedApi } = useAuth();
    const [devices, setDevices] = useState([]);
    const [accessMode, setAccessMode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [creating, setCreating] = useState(false);
    const [created, setCreated] = useState(null);
    const [disableTarget, setDisableTarget] = useState(null);
    const [deviceType, setDeviceType] = useState('labkey');
    const [labkey, setLabkey] = useState(emptyLabkey);
    const [editTarget, setEditTarget] = useState(null);
    const [editValues, setEditValues] = useState(emptyLabkey);
    const [saving, setSaving] = useState(false);

    const api = getAuthenticatedApi();
    const endpoint = `${String(api.defaults?.baseURL || '').replace(/\/$/, '')}/gym_access_scan.php`;

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [devRes, gymRes] = await Promise.all([
                api.get('/gym_access.php', { params: { action: 'devices', gym_id: gymId } }),
                api.get(`/gyms.php?id=${gymId}`),
            ]);
            setDevices(Array.isArray(devRes.data?.devices) ? devRes.data.devices : []);
            const gym = gymRes.data?.gym || gymRes.data;
            setAccessMode(gym?.access_mode ?? null);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            toast.error(err.response?.data?.error || 'Errore nel caricamento dei terminali');
        } finally {
            setLoading(false);
        }
    }, [api, gymId]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gymId]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        try {
            setCreating(true);
            const body = { gym_id: Number(gymId), name: name.trim() };
            if (deviceType === 'labkey') {
                Object.assign(body, {
                    device_type: 'labkey',
                    labkey_unique_name: labkey.unique.trim(),
                    labkey_wifipass: labkey.wifipass,
                    labkey_passkey: labkey.passkey.trim(),
                    labkey_relay: Number(labkey.relay) || 1,
                });
            }
            const res = await api.post('/gym_access.php?action=create_device', body);
            if (!res.data?.success) throw new Error(res.data?.error || 'Errore');
            setCreated(deviceType === 'labkey'
                ? { name: res.data.device.name, labkey: true, passkey: res.data.labkey_passkey }
                : { name: res.data.device.name, token: res.data.token });
            setName('');
            setLabkey(emptyLabkey);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Errore nella creazione del terminale');
        } finally {
            setCreating(false);
        }
    };

    const handleDisable = async () => {
        if (!disableTarget) return;
        try {
            await api.post('/gym_access.php?action=disable_device', { id: disableTarget.id });
            toast.success(`Terminale "${disableTarget.name}" disattivato`);
            setDisableTarget(null);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Errore nella disattivazione');
        }
    };

    const openEdit = (d) => {
        setEditTarget(d);
        setEditValues({ ...emptyLabkey, relay: String(d.labkey_relay || 1) });
    };

    const handleSaveLabkey = async (e) => {
        e.preventDefault();
        if (!editTarget) return;
        try {
            setSaving(true);
            const body = { id: editTarget.id, labkey_relay: Number(editValues.relay) || 1 };
            if (editValues.wifipass) body.labkey_wifipass = editValues.wifipass;
            if (editValues.passkey.trim()) body.labkey_passkey = editValues.passkey.trim();
            const res = await api.post('/gym_access.php?action=update_labkey', body);
            if (!res.data?.success) throw new Error(res.data?.error || 'Errore');
            toast.success('Dati LabKey salvati');
            if (res.data.labkey_passkey) {
                setCreated({ name: editTarget.name, labkey: true, passkey: res.data.labkey_passkey });
            }
            setEditTarget(null);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Errore nel salvataggio');
        } finally {
            setSaving(false);
        }
    };

    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copiato`);
        } catch {
            toast.error('Copia non riuscita: selezionalo e copialo a mano');
        }
    };

    if (loading) {
        return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;
    }

    return (
        <div className="space-y-4" data-testid="admin-access-devices">
            {accessMode !== 'terminal' && (
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                        Il controllo accessi di questa palestra è spento: i terminali rifiutano tutti gli ingressi.
                        Accendilo da <strong>Gestione palestre → Modifica</strong>.
                    </span>
                </div>
            )}

            {created?.labkey && (
                <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-100" data-testid="labkey-created-box">
                    <div className="mb-2 flex items-center gap-2 font-semibold">
                        <CheckCircle2 className="h-4 w-4" />
                        Centralina "{created.name}" pronta. Nella centralina, in Chiamate custom → Delega su dispositivo esterno, inserisci:
                    </div>
                    <dl className="space-y-2">
                        {[['URL', endpoint], ['URL keep alive', endpoint], ...(created.passkey ? [['PassKey', created.passkey]] : [])].map(([label, value]) => (
                            <div key={label}>
                                <dt className="text-xs uppercase tracking-wide opacity-70">{label}</dt>
                                <dd className="flex items-center gap-2">
                                    <code className="min-w-0 flex-1 break-all rounded bg-white/70 px-2 py-1 font-mono text-xs dark:bg-black/30" data-testid={`labkey-${label.toLowerCase().replace(/ /g, '-')}`}>{value}</code>
                                    <button type="button" onClick={() => copy(value, label)} className="rounded p-1.5 hover:bg-emerald-100 dark:hover:bg-emerald-900" aria-label={`Copia ${label}`}>
                                        <Copy className="h-4 w-4" />
                                    </button>
                                </dd>
                            </div>
                        ))}
                    </dl>
                    <p className="mt-2 text-xs opacity-80">
                        {created.passkey
                            ? 'La PassKey generata si vede solo adesso: copiala nella centralina.'
                            : 'Usa nella centralina la stessa PassKey che hai inserito qui.'}
                        {' '}Attiva anche i codici custom.
                    </p>
                    <button type="button" onClick={() => setCreated(null)} className="mt-3 text-xs font-medium underline">
                        Fatto, chiudi
                    </button>
                </div>
            )}

            {created && !created.labkey && (
                <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-100" data-testid="device-token-box">
                    <div className="mb-2 flex items-center gap-2 font-semibold">
                        <CheckCircle2 className="h-4 w-4" />
                        Terminale "{created.name}" creato. Copia il token adesso: non sarà più visibile.
                    </div>
                    <dl className="space-y-2">
                        {[['Token', created.token], ['Endpoint', endpoint]].map(([label, value]) => (
                            <div key={label}>
                                <dt className="text-xs uppercase tracking-wide opacity-70">{label}</dt>
                                <dd className="flex items-center gap-2">
                                    <code className="min-w-0 flex-1 break-all rounded bg-white/70 px-2 py-1 font-mono text-xs dark:bg-black/30" data-testid={`device-${label.toLowerCase()}`}>{value}</code>
                                    <button type="button" onClick={() => copy(value, label)} className="rounded p-1.5 hover:bg-emerald-100 dark:hover:bg-emerald-900" aria-label={`Copia ${label}`}>
                                        <Copy className="h-4 w-4" />
                                    </button>
                                </dd>
                            </div>
                        ))}
                    </dl>
                    <p className="mt-2 text-xs opacity-80">Il terminale manda il token nell'header <code>X-Device-Token</code>.</p>
                    <button type="button" onClick={() => setCreated(null)} className="mt-3 text-xs font-medium underline">
                        Ho copiato il token, chiudi
                    </button>
                </div>
            )}

            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow dark:border-gray-800 dark:bg-gray-900">
                <div className="mb-3 flex items-center gap-2">
                    <DoorOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Terminali controllo accessi</h3>
                </div>

                <form onSubmit={handleCreate} className="mb-4 space-y-2">
                    <div className="flex flex-col gap-2 sm:flex-row">
                    <select value={deviceType} onChange={(e) => setDeviceType(e.target.value)} className={inputClass} data-testid="device-type">
                        <option value="labkey">Centralina LabKey</option>
                        <option value="generic">Terminale generico (token)</option>
                    </select>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Nome terminale (es. Ingresso principale)"
                        maxLength={100}
                        className={`flex-1 ${inputClass}`}
                    />
                    </div>
                    {deviceType === 'labkey' && <LabkeyFields value={labkey} onChange={setLabkey} />}
                    <button
                        type="submit"
                        disabled={creating || !name.trim() || (deviceType === 'labkey' && (!labkey.unique.trim() || !labkey.wifipass))}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        <Plus className="h-4 w-4" />
                        Crea terminale
                    </button>
                </form>

                {devices.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Nessun terminale per questa palestra.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                <th className="py-2">Nome</th>
                                <th className="py-2">Stato</th>
                                <th className="hidden py-2 sm:table-cell">Ultimo contatto</th>
                                <th className="hidden py-2 md:table-cell">Creato</th>
                                <th className="py-2" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {devices.map((d) => (
                                <tr key={d.id} data-testid="device-row">
                                    <td className="py-2 font-medium text-gray-900 dark:text-gray-100">
                                        {d.name}
                                        {d.device_type === 'labkey' && (
                                            <div className="text-xs font-normal text-gray-500 dark:text-gray-400">
                                                LabKey {d.labkey_unique_name} · relè {d.labkey_relay}
                                                {!d.labkey_configured && <span className="text-amber-600"> · dati mancanti</span>}
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-2">
                                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${d.is_active
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                                            : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}
                                        >
                                            {d.is_active ? 'Attivo' : 'Disattivato'}
                                        </span>
                                    </td>
                                    <td className="hidden py-2 text-gray-600 dark:text-gray-300 sm:table-cell">{formatDateTime(d.last_seen_at)}</td>
                                    <td className="hidden py-2 text-gray-600 dark:text-gray-300 md:table-cell">{formatDateTime(d.created_at)}</td>
                                    <td className="py-2 text-right">
                                        {d.is_active && d.device_type === 'labkey' && (
                                            <button
                                                type="button"
                                                onClick={() => openEdit(d)}
                                                className="mr-2 inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                                            >
                                                <KeyRound className="h-3.5 w-3.5" />
                                                Dati LabKey
                                            </button>
                                        )}
                                        {d.is_active && (
                                            <button
                                                type="button"
                                                onClick={() => setDisableTarget(d)}
                                                className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-950/40"
                                            >
                                                <Power className="h-3.5 w-3.5" />
                                                Disattiva
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editTarget && (
                <form onSubmit={handleSaveLabkey} className="space-y-2 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-500/40 dark:bg-blue-950/30" data-testid="labkey-edit">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Dati LabKey di "{editTarget.name}" ({editTarget.labkey_unique_name})
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                        Se cambi PassKey o password qui, cambiale uguali anche nella centralina, altrimenti il varco smette di aprire.
                    </p>
                    <LabkeyFields value={editValues} onChange={setEditValues} editing />
                    <div className="flex gap-2">
                        <button type="submit" disabled={saving} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                            Salva
                        </button>
                        <button type="button" onClick={() => setEditTarget(null)} className="rounded-md px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800">
                            Annulla
                        </button>
                    </div>
                </form>
            )}

            <ConfirmModal
                isOpen={Boolean(disableTarget)}
                onClose={() => setDisableTarget(null)}
                onConfirm={handleDisable}
                title="Disattivare il terminale?"
                message={`"${disableTarget?.name || ''}" smetterà subito di aprire il varco. Per riattivarlo serve crearne uno nuovo.`}
                confirmText="Disattiva"
                cancelText="Annulla"
                variant="warning"
            />
        </div>
    );
}
